import { X, LoaderCircle } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface FileUploadProps {
  onFileUpload: (files: File[]) => void;
  isLoading?: boolean;
  selectedFiles: File[];
  onSetSelectedFiles: Dispatch<SetStateAction<File[]>>;
  uploadProgress?: number | null;
}

export function FileUpload({
  onFileUpload,
  selectedFiles,
  isLoading = false,
  onSetSelectedFiles,
  uploadProgress = null,
}: FileUploadProps) {
  const MAX_FILES = 500;
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ImagesManager.components.FileUpload',
  });
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      const files = acceptedFiles.slice(0, MAX_FILES);
      onSetSelectedFiles(files);
    },
    [onSetSelectedFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    maxFiles: MAX_FILES,
  });

  function clearFiles() {
    onSetSelectedFiles([]);
  }

  async function handleUpload() {
    if (selectedFiles.length > 0 && !isLoading) {
      onFileUpload(selectedFiles);
    }
  }

  const previews = useMemo(() => {
    return selectedFiles.slice(0, 12).map((f) => ({
      name: f.name,
      url: URL.createObjectURL(f),
      size: f.size,
    }));
  }, [selectedFiles]);

  return (
    <div className="flex flex-row items-end gap-4">
      <div
        {...getRootProps()}
        className={cn(
          'w-full cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          isDragActive
            ? 'border-primary bg-black/5'
            : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-black/5',
        )}
      >
        <input {...getInputProps()} disabled={isLoading} />
        {isLoading ? (
          <>
            <LoaderCircle className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
            <p className="font-medium text-primary">{t('uploadingMessage')}</p>
            {typeof uploadProgress === 'number' && (
              <div className="mx-auto mt-3 h-2 w-1/2 overflow-hidden rounded bg-muted">
                <div
                  className="h-2 bg-primary transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </>
        ) : isDragActive ? (
          <p className="text-primary">{t('dropZoneActive')}</p>
        ) : (
          <div>
            <p className="mb-2 font-medium text-foreground">
              {t('dropZoneInactive')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('dropZoneSupport')}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {selectedFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">{t('selectedFilesTitle')}</h4>
            <div className="grid max-h-40 grid-cols-3 gap-2 overflow-auto rounded bg-muted p-2">
              {previews.map((p) => (
                <img
                  key={p.name}
                  src={p.url}
                  alt={p.name}
                  className="h-20 w-full rounded object-cover"
                />
              ))}
              {selectedFiles.length > previews.length && (
                <div className="flex h-20 items-center justify-center rounded bg-background text-xs text-muted-foreground">
                  +{selectedFiles.length - previews.length}
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {selectedFiles.length} / {MAX_FILES}
            </div>
            <Button
              onClick={handleUpload}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading && (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isLoading ? t('uploadingButton') : t('uploadButton')}
            </Button>
            <Button
              variant="ghost"
              onClick={clearFiles}
              className="w-full"
              disabled={isLoading}
            >
              <X className="mr-2 h-4 w-4" />
              {t('clearSelectionButton')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
